import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { Label, Paragraph, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { AttachmentPicker } from '@/components/attachment-picker';
import { CategoryChip } from '@/components/category-chip';
import { Input } from '@/components/input';
import { LocationCard } from '@/components/location-card';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { ScreenHeader } from '@/components/screen-header';
import { TextArea } from '@/components/text-area';
import { useAttachmentPicker } from '@/hooks/use-attachment-picker';
import { useCurrentLocation } from '@/hooks/use-current-location';
import { api, type ThreadCategory } from '@/lib/api';
import { MAX_THREAD_BODY_LEN, MAX_THREAD_TITLE_LEN } from '@/lib/content-limits';
import { queryKeys } from '@/lib/query-keys';
import { categoryFromParam, THREAD_CATEGORIES } from '@/lib/thread-category';
import { borderWidth, space } from '@/theme/tokens.generated';

// Real screen, not a stub -- issue #15's create slice.
//
// Location here is a per-thread, point-in-time read (this thread's
// coordinates at the moment it's posted), unlike the profile screen's
// location, which is a persisted setting a user revisits and updates.
// Both screens now read location through the same useCurrentLocation
// hook (see that file's doc comment for why this became its second
// call site instead of a duplicate) but present it differently: this
// screen has no "Update" affordance, since re-requesting mid-draft
// would silently change what the thread claims to be near without the
// user touching title or body.
export function CreateThread() {
  const router = useRouter();
  const queryClient = useQueryClient();

  // The Threads filter travels here as a route param (issue #411) so the
  // composer opens on the category the user was browsing, title seeded the
  // same way tapping its chip would.
  const initialCategory = categoryFromParam(useLocalSearchParams<{ category?: string }>().category);
  const [title, setTitle] = useState(THREAD_CATEGORIES.find((c) => c.value === initialCategory)?.starter ?? '');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<ThreadCategory | undefined>(initialCategory);
  const location = useCurrentLocation();

  // Issue #287: both error strings below used to render unconditionally
  // whenever the field was empty -- including on first paint, before
  // the user had typed anything. A pristine, untouched form opened
  // already showing two red errors, which is exactly backwards for the
  // highest-stakes authoring moment in the app (broadcasting to real,
  // physically nearby strangers) and destroys the signal value of red
  // for when a REAL mistake happens. `touched` gates each error on the
  // user having actually interacted with that field (blur) -- the
  // standard fix for this class of bug, same as any real form
  // validation library's default behavior.
  const [titleTouched, setTitleTouched] = useState(false);
  const [bodyTouched, setBodyTouched] = useState(false);

  const titleValid = title.trim() !== '';
  const bodyValid = body.trim() !== '';
  const attachments = useAttachmentPicker();
  // Not while a photo is still uploading: it would be posted without it.
  const canSubmit = titleValid && bodyValid && location.phase.phase === 'granted' && !attachments.isUploading;

  const create = useMutation({
    mutationFn: () => {
      if (location.phase.phase !== 'granted') {
        // Unreachable through the UI (the button guard below never
        // fires mutate() before location is granted) -- a defensive
        // guard against ever sending the silent 0,0 default the brief
        // explicitly rules out, not a real user-facing path. Matches
        // the profile screen's update mutation precedent.
        throw new Error('Location is required before posting a thread.');
      }

      return api.createThread({
        title: title.trim(),
        body: body.trim(),
        lat: location.phase.coords.lat,
        lng: location.phase.coords.lng,
        category,
        photoBlobIds: attachments.photoIds,
      });
    },
    onSuccess: (thread) => {
      // Task 4's thread list screen keys its query on ['threads'] --
      // invalidating here means the list reflects this new thread the
      // next time it's shown, rather than a stale page that predates
      // it (same reasoning as the profile screen's onSuccess).
      queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      // The screen stays mounted under the pushed detail; without this a
      // back-swipe shows the just-posted photos still attached.
      attachments.reset();
      router.push({ pathname: '/threads/[id]', params: { id: thread.id } });
    },
  });

  return (
    <ScreenContainer>
      {/* 'padding' is the documented iOS behavior. Android is
          deliberately left undefined, not a gap: app.json sets no
          android.softwareKeyboardLayoutMode override, so Expo's own
          default ("resize", i.e. windowSoftInputMode="adjustResize")
          already shrinks the window natively when the keyboard opens --
          layering RN's own 'height' behavior on top would double-apply
          the resize (a documented RN/Expo footgun), not fix anything.
          The 2026-09-03 UI/UX audit flagged this as a gap and an
          initial fix set 'height' here; caught and reverted in review
          (PR #61) once checked against the actual Expo config instead
          of assumed. */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* Everything -- header, fields, AND the Post-thread footer --
            lives inside one ScrollView, unlike profile/index.tsx's
            fixed-header/scrolling-middle/fixed-footer split. This
            screen previously had NO scroll container at all: a fixed
            YStack held the title/body/location fields with flex:1, and
            a separate footer YStack sat below it -- the exact failure
            profile/index.tsx's own ScrollView fix already documents
            (neither section shrinks by default, so overflow doesn't
            scroll, it silently overlaps). Live-reported here as "Post
            thread" and "Share location" rendering superposed, with no
            way to scroll at all. Putting the FOOTER inside the scroll
            too (not just the fields, as profile does) is deliberate:
            a short viewport plus an open keyboard can still push the
            button out of reach even with the fields scrollable on
            their own, and the button is the one thing on this screen
            that must always be reachable -- founder-reported, 2026-09-15
            ("should be able to at least scroll past the buttons"). */}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingTop: space[6], paddingBottom: space[16] + space[8], gap: space[6] }}
          keyboardShouldPersistTaps="handled"
        >
          {/* This screen's own dismiss action -- (app)/_layout.tsx sets
              headerShown: false for threads/new (its duplicate native
              title matched this heading, whole-app critique
              2026-09-02), which also removed the native header's back
              button, the only way out on web. Shared BackLink
              (src/components/back-link.tsx), same pattern as sign-up's
              CloseLink -- "Cancel" since this abandons an in-progress
              draft, not something already posted. */}
          <ScreenHeader
            label="Cancel"
            title="New thread"
            subtitle="Share something with people nearby. Your thread is visible to anyone whose search radius covers where you are right now."
            onBack={() => router.back()}
          />

          <YStack gap="$4">
            <YStack gap="$2">
              <Label color="$colorSecondary">Category (optional)</Label>
              {/* Tapping a chip sets the category and seeds Title with
                  that category's scaffold -- never Body (issue #266's
                  own plan decision). Replaces whatever was already
                  typed: picking a category is choosing a template, not
                  appending to one. flexWrap lets the 7 chips wrap to
                  more than one row on a narrow viewport rather than
                  overflowing horizontally (components.md's
                  narrow-width defences). */}
              <XStack flexWrap="wrap" gap="$2">
                {THREAD_CATEGORIES.map((c) => (
                  <CategoryChip
                    key={c.value}
                    label={c.label}
                    category={c.value}
                    selected={category === c.value}
                    onPress={() => {
                      setCategory(c.value);
                      setTitle(c.starter);
                    }}
                  />
                ))}
              </XStack>
            </YStack>

            <YStack gap="$1">
              <Label htmlFor="thread-title" color="$colorSecondary">
                Title
              </Label>
              <Input
                id="thread-title"
                value={title}
                onChangeText={setTitle}
                onBlur={() => setTitleTouched(true)}
                placeholder="What's going on?"
                autoCapitalize="sentences"
                maxLength={MAX_THREAD_TITLE_LEN}
              />
              {/* Three fields (title, body, location) all gate "Post
                  thread" with no prior indication of which is missing --
                  same silent-disabled-button gap fixed on profile's
                  Handle and sign-up's Email (2026-09-03 audit). Gated on
                  `titleTouched` (see this screen's own doc comment above
                  `titleTouched`'s declaration) -- the guidance itself is
                  still valuable once the user has actually engaged with
                  the field, it just shouldn't greet them on arrival. */}
              {titleTouched && !titleValid && (
                <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
                  Enter a title to continue.
                </Text>
              )}
            </YStack>

            <YStack gap="$1">
              {/* Decision 8 (issue #569): the add-photo control surfaces
                  beside the details field -- the composer's whole photo
                  section used to sit below the fold at 390px, so the form
                  read as title/details only. The picker (44px add button,
                  thumbnails, its own error line) mounts in the Details
                  label row; same widget and behaviour, only the placement
                  changed. */}
              <XStack alignItems="center" justifyContent="space-between" gap="$2">
                <Label htmlFor="thread-body" color="$colorSecondary">
                  Details
                </Label>
                <AttachmentPicker picker={attachments} locked={create.isPending} />
              </XStack>
              <TextArea
                id="thread-body"
                value={body}
                onChangeText={setBody}
                onBlur={() => setBodyTouched(true)}
                placeholder="Add the details people nearby should know..."
                minHeight={120}
                maxLength={MAX_THREAD_BODY_LEN}
              />
              {bodyTouched && !bodyValid && (
                <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
                  Add some details to continue.
                </Text>
              )}
            </YStack>

            <YStack gap="$2">
              <Label color="$colorSecondary">Location</Label>
              <LocationCard location={location} context="post this thread nearby" />
            </YStack>
          </YStack>

          <YStack gap="$3" pt="$4" borderTopWidth={borderWidth.hairline} borderTopColor="$borderColor">
            {create.isError && (
              <Paragraph color="$destructive" textAlign="center">
                {create.error.message || "Couldn't post your thread. Check your connection and try again."}
              </Paragraph>
            )}

            {/* Issue #152 AC2: TanStack's default networkMode: 'online'
                already holds this mutation 'paused' (not fired, not
                errored) while offline and resumes it automatically on
                reconnect (network.ts's onlineManager wiring is what
                makes that real in React Native) -- this just tells the
                user plainly what's happening instead of leaving them
                staring at an indefinite spinner with no explanation. */}
            {create.isPaused && (
              <Paragraph color="$colorSecondary" textAlign="center">
                You&apos;re offline. This will post as soon as you&apos;re back online.
              </Paragraph>
            )}

            <PrimaryButton
              disabled={!canSubmit}
              loading={create.isPending && !create.isPaused}
              onPress={() => {
                if (!create.isPending && canSubmit) create.mutate();
              }}
            >
              {create.isPaused ? (
                'Waiting to reconnect...'
              ) : create.isPending ? (
                <ActivityIndicator color="white" />
              ) : (
                'Post thread'
              )}
            </PrimaryButton>
          </YStack>
        </ScrollView>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
